import * as React from 'react';
import { ButtonType, TabStyles } from '@tableau/widgets';

import { Messages } from '~src/codegen/Localize';
import { cssClass } from '~src/style';
import { ATTENTION_COLOR } from '~src/settings/styles/CommonStyles';
import { ButtonWithSpinner } from '~src/common/react/components/ButtonWithSpinner';
import { assertUnreachable } from '~src/ts/helpers/TypescriptHelpers';
import { EditOAuthConnectionDialogState } from '../../EditOAuthConnectionAction';
import { ITestConnectionResult } from '~src/connectivity/services/EditConnectionDialog';

export type EditOAuthConnectionDialogTestConnectionProps = Readonly<{
    dialogState: EditOAuthConnectionDialogState;
    onTestConnection: (dialogState: EditOAuthConnectionDialogState) => Promise<ITestConnectionResult>;
    isTestConnectionDisabled: boolean;
}>;


type TestConnectionAction =
    { type: 'TEST_CONNECTION_START' } |
    { type: 'TEST_CONNECTION_FINISH', result: ITestConnectionResult };

type TestConnectionState =
    | { name: 'idle' | 'testing' }
    | {
        name: 'testComplete';
        succeeded: boolean;
        connectionTestMessage: string;
    }
    | { name: 'unexpectedError' };


function reducer(state: TestConnectionState, action: TestConnectionAction): TestConnectionState {
    switch (action.type) {
        case 'TEST_CONNECTION_START':
            return { name: 'testing' };
        case 'TEST_CONNECTION_FINISH':
            if (!action.result) {
                return { name: 'unexpectedError' };
            }
            return {
                name: 'testComplete',
                succeeded: (action.result && action.result.succeeded),
                connectionTestMessage: (action.result && action.result.messages && action.result.messages.length > 0 && action.result.messages[0])
            };
        default:
            return state;
    }
}

export function EditOAuthConnectionDialogTestConnection(props: EditOAuthConnectionDialogTestConnectionProps) {

    const [state, dispatch] = React.useReducer(
        reducer,
        { name: 'idle' }
    );

    const onTestConnection = async () => {
        dispatch({ type: 'TEST_CONNECTION_START' });
        const testConnectionResult = await props.onTestConnection(props.dialogState);
        dispatch({ type: 'TEST_CONNECTION_FINISH', result: testConnectionResult });
    };


    const renderButton = () => {
        return (
            <div style={{ paddingTop: '24px' }}>
                <ButtonWithSpinner
                    buttonType={ButtonType.Outline}
                    clicked={onTestConnection}
                    testId="test-conn"
                    data-tb-test-id='oauth-test-conn'
                    showSpinner={state.name === 'testing'}
                    title={Messages.testConnection()}
                    disabled={props.isTestConnectionDisabled}
                />
            </div>
        );
    };

    const renderConnectionTestOutcome = () => {
        switch (state.name) {
            case 'testComplete':
                const connectionMessage = state.succeeded ? Messages.testConnectionSuccess() : Messages.testConnectionFailure();
                const testOutcomeClassname: string = cssClass('edit-connection', 'test-outcome', {
                    color: state.succeeded ? TabStyles.Typography.Color.Primary : ATTENTION_COLOR
                });
                return (
                    <div className={testOutcomeClassname} style={{ paddingTop: '6px' }} data-tb-test-id='edit-oauth-text'>
                        {connectionMessage}
                        {state.connectionTestMessage}
                    </div>
                );
            case 'idle':
            case 'testing':
            case 'unexpectedError':
                return null;
            default:
                assertUnreachable(state);
                return null;
        }
    };

    return (
        <div style={{ textAlign: 'right', paddingBottom: '16px' }} data-tb-test-id='edit-oauth-button-signin-and-text'>
            {renderButton()}
            {renderConnectionTestOutcome()}
        </div>
    );

}
